import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  Loader2,
  Check,
  Video,
  Image,
  FileText,
  Sparkles,
  Tag,
  Send,
  X,
  Play,
  Music,
  RefreshCw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ALL_STEPS = [
  { id: 1, title: "Backgrounds", description: "Upload per-scene backgrounds" },
  { id: 2, title: "Text Overlays", description: "Add text for each scene" },
  { id: 3, title: "Product", description: "Upload product image" },
  { id: 4, title: "Composites", description: "Generate scene composites" },
  { id: 5, title: "Video Prompts", description: "Scene video prompts" },
  { id: 6, title: "Generate Videos", description: "Generate scene videos" },
  { id: 7, title: "Audio", description: "Upload background music" },
  { id: 8, title: "Preview", description: "Stitch & preview" },
  { id: 9, title: "Details", description: "Title, tags & description" },
  { id: 10, title: "Publish", description: "Review & publish" },
];

const HERO_SHOT_STEPS = [
  { id: 1, title: "Background", description: "Upload scene background" },
  { id: 2, title: "Text Overlay", description: "Add text for the scene" },
  { id: 3, title: "Product", description: "Upload product image" },
  { id: 4, title: "Composite", description: "Generate scene composite" },
  { id: 5, title: "Video Prompt", description: "Scene video prompt" },
  { id: 6, title: "Generate Video", description: "Generate scene video" },
  { id: 7, title: "Audio", description: "Upload background music" },
  { id: 8, title: "Preview", description: "Generate final preview" },
  { id: 9, title: "Details", description: "Title, tags & description" },
  { id: 10, title: "Publish", description: "Review & publish" },
];

export default function ProductVideoCreator({ template, setTemplate }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const isHeroShot = template?.template_type === "hero_shot";
  const STEPS = isHeroShot ? HERO_SHOT_STEPS : ALL_STEPS;

  const sceneCount = template?.scene_count || (isHeroShot ? 1 : 2);

  // Per-scene states
  const [bgFiles, setBgFiles] = useState(Array(sceneCount).fill(null));
  const [bgPreviews, setBgPreviews] = useState(Array(sceneCount).fill(null));
  const [textOverlays, setTextOverlays] = useState(Array(sceneCount).fill(""));
  const [compositePrompts, setCompositePrompts] = useState(Array(sceneCount).fill(""));
  const [videoPrompts, setVideoPrompts] = useState(Array(sceneCount).fill(""));
  const [sceneJobIds, setSceneJobIds] = useState(Array(sceneCount).fill(null));
  const [sceneJobStatuses, setSceneJobStatuses] = useState(Array(sceneCount).fill(null));

  // Other states
  const [productFile, setProductFile] = useState(null);
  const [productPreview, setProductPreview] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [stitchJobId, setStitchJobId] = useState(null);
  const [stitchStatus, setStitchStatus] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [creditsCost, setCreditsCost] = useState("");
  const [productType, setProductType] = useState("");
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [videoMethod, setVideoMethod] = useState(template?.template_type === "hero_shot" ? "gemini_veo" : "fal_kling");

  // Initialize from template data (edit mode)
  useEffect(() => {
    if (template) {
      const sc = template.scene_count || 2;
      if (template.scene_backgrounds?.length) {
        setBgPreviews(template.scene_backgrounds.map(u => u || null));
      }
      if (template.scene_text_overlays?.length) {
        setTextOverlays(prev => {
          const arr = [...prev];
          template.scene_text_overlays.forEach((t, i) => { if (i < sc) arr[i] = t || ""; });
          return arr;
        });
      }
      if (template.scene_composite_prompts?.length) {
        setCompositePrompts(prev => {
          const arr = [...prev];
          template.scene_composite_prompts.forEach((p, i) => { if (i < sc) arr[i] = p || ""; });
          return arr;
        });
      }
      if (template.scene_video_prompts?.length) {
        setVideoPrompts(prev => {
          const arr = [...prev];
          template.scene_video_prompts.forEach((p, i) => { if (i < sc) arr[i] = p || ""; });
          return arr;
        });
      }
      if (template.sample_product_image) setProductPreview(template.sample_product_image);
      if (template.title) setTitle(template.title);
      if (template.description) setDescription(template.description);
      if (template.product_type) setProductType(template.product_type);
      if (template.credits_cost != null) setCreditsCost(String(template.credits_cost));
      if (template.tags?.length) setTags(template.tags);
      if (template.video_method) setVideoMethod(template.video_method);
    }
  }, [template]);

  // ============ STEP 1: Upload Backgrounds ============
  const handleBgChange = (index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      const newFiles = [...bgFiles];
      newFiles[index] = file;
      setBgFiles(newFiles);
      const newPreviews = [...bgPreviews];
      newPreviews[index] = URL.createObjectURL(file);
      setBgPreviews(newPreviews);
    }
  };

  const uploadBackground = async (index) => {
    if (!bgFiles[index] || !template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", bgFiles[index]);
      formData.append("scene_index", index);
      const resp = await axios.post(
        `${API}/api/templates/${template.id}/upload-scene-background`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      const bgs = [...(template.scene_backgrounds || [])];
      while (bgs.length <= index) bgs.push(null);
      bgs[index] = resp.data.background_url;
      setTemplate({ ...template, scene_backgrounds: bgs });
      toast.success(`Scene ${index + 1} background uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadAllBackgrounds = async () => {
    for (let i = 0; i < sceneCount; i++) {
      if (bgFiles[i]) await uploadBackground(i);
      else if (!template?.scene_backgrounds?.[i]) {
        toast.error(`Scene ${i + 1} background is required`);
        return;
      }
    }
    setCurrentStep(2);
  };

  // ============ STEP 2: Save Text Overlays ============
  const saveTextOverlays = async () => {
    if (!template) return;
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/templates/${template.id}/save-scene-text-overlays`,
        { text_overlays: textOverlays.slice(0, sceneCount) },
        { withCredentials: true }
      );
      setTemplate({ ...template, scene_text_overlays: textOverlays.slice(0, sceneCount) });
      toast.success("Text overlays saved");
      setCurrentStep(3);
    } catch (err) {
      toast.error("Failed to save text overlays");
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 3: Upload Product ============
  const uploadProduct = async () => {
    if (!productFile || !template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", productFile);
      const resp = await axios.post(
        `${API}/api/templates/${template.id}/upload-sample-product`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setTemplate({ ...template, sample_product_image: resp.data.sample_product_image });
      toast.success("Product image uploaded");
      setCurrentStep(4);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 4: Generate Composites ============
  const generateComposite = async (index) => {
    if (!template || !compositePrompts[index]?.trim()) {
      toast.error("Enter a prompt first");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("scene_index", index);
      formData.append("prompt", compositePrompts[index]);
      const resp = await axios.post(
        `${API}/api/templates/${template.id}/generate-scene-composite`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      // Poll for completion
      const jobId = resp.data.job_id;
      toast.info(`Scene ${index + 1} composite generation started...`);
      const pollInterval = setInterval(async () => {
        try {
          const jobResp = await axios.get(
            `${API}/api/templates/${template.id}/composite-job/${jobId}`,
            { withCredentials: true }
          );
          const job = jobResp.data;
          if (job.status === "completed") {
            clearInterval(pollInterval);
            const tResp = await axios.get(`${API}/api/templates/${template.id}`, { withCredentials: true });
            const t = tResp.data.template || tResp.data;
            setTemplate(t);
            setLoading(false);
            toast.success(`Scene ${index + 1} composite generated`);
          } else if (job.status === "failed") {
            clearInterval(pollInterval);
            setLoading(false);
            toast.error(job.message || "Generation failed");
          }
        } catch {
          clearInterval(pollInterval);
          setLoading(false);
        }
      }, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
      setLoading(false);
    }
  };

  // ============ STEP 5: Save Video Prompts ============
  const saveVideoPrompts = async () => {
    if (!template) return;
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/templates/${template.id}/save-scene-video-prompts`,
        { video_prompts: videoPrompts.slice(0, sceneCount) },
        { withCredentials: true }
      );
      setTemplate({ ...template, scene_video_prompts: videoPrompts.slice(0, sceneCount) });
      toast.success("Video prompts saved");
      setCurrentStep(6);
    } catch (err) {
      toast.error("Failed to save prompts");
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 6: Generate Scene Videos ============
  const generateSceneVideo = async (index) => {
    if (!template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("scene_index", index);
      formData.append("video_method", videoMethod);
      const resp = await axios.post(
        `${API}/api/templates/${template.id}/generate-scene-video`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      const newJobs = [...sceneJobIds];
      newJobs[index] = resp.data.job_id;
      setSceneJobIds(newJobs);
      const newStatuses = [...sceneJobStatuses];
      newStatuses[index] = { status: "processing", progress: 0, message: "Starting..." };
      setSceneJobStatuses(newStatuses);
      toast.info(`Scene ${index + 1} video generation started`);
      pollSceneVideoJob(index, resp.data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start video generation");
    } finally {
      setLoading(false);
    }
  };

  const pollSceneVideoJob = async (index, jobId) => {
    const poll = async () => {
      try {
        const resp = await axios.get(
          `${API}/api/templates/${template.id}/scene-video-job/${jobId}`,
          { withCredentials: true }
        );
        const job = resp.data;
        const newStatuses = [...sceneJobStatuses];
        newStatuses[index] = job;
        setSceneJobStatuses(newStatuses);

        if (job.status === "completed") {
          const vids = [...(template.scene_videos || [])];
          while (vids.length <= index) vids.push(null);
          vids[index] = job.result?.video_url;
          setTemplate({ ...template, scene_videos: vids });
          toast.success(`Scene ${index + 1} video ready`);
          return;
        }
        if (job.status === "failed") {
          toast.error(`Scene ${index + 1} failed: ${job.message}`);
          return;
        }
        setTimeout(poll, 5000);
      } catch {
        setTimeout(poll, 5000);
      }
    };
    poll();
  };

  // ============ STEP 7: Upload Audio ============
  const uploadAudio = async () => {
    if (!audioFile || !template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", audioFile);
      const resp = await axios.post(
        `${API}/api/templates/${template.id}/upload-audio`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setTemplate({ ...template, audio_file_url: resp.data.audio_file_url });
      toast.success("Audio uploaded");
      setCurrentStep(isHeroShot ? 9 : 8);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 8: Stitch Preview ============
  const stitchPreview = async () => {
    if (!template) return;
    setLoading(true);
    try {
      const resp = await axios.post(
        `${API}/api/templates/${template.id}/stitch-preview`,
        {},
        { withCredentials: true }
      );
      setStitchJobId(resp.data.job_id);
      setStitchStatus({ status: "processing", progress: 0, message: "Starting..." });
      toast.info("Stitching started...");
      pollStitchJob(resp.data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start stitching");
    } finally {
      setLoading(false);
    }
  };

  const pollStitchJob = async (jobId) => {
    const poll = async () => {
      try {
        const resp = await axios.get(
          `${API}/api/templates/${template.id}/stitch-job/${jobId}`,
          { withCredentials: true }
        );
        setStitchStatus(resp.data);
        if (resp.data.status === "completed") {
          setTemplate({ ...template, sample_video: resp.data.result?.video_url });
          toast.success("Preview video ready!");
          return;
        }
        if (resp.data.status === "failed") {
          toast.error(`Stitching failed: ${resp.data.message}`);
          return;
        }
        setTimeout(poll, 5000);
      } catch {
        setTimeout(poll, 5000);
      }
    };
    poll();
  };

  // ============ STEP 9: Save Details ============
  const saveDetails = async () => {
    if (!template || !title.trim()) {
      toast.error("Title is required");
      return;
    }
    setLoading(true);
    try {
      await axios.put(
        `${API}/api/templates/${template.id}`,
        { title, description, tags, credits_cost: creditsCost ? parseFloat(creditsCost) : null, product_type: productType.trim() || null },
        { withCredentials: true }
      );
      setTemplate({ ...template, title, description, tags, credits_cost: creditsCost ? parseFloat(creditsCost) : null, product_type: productType.trim() || null });
      toast.success("Details saved");
      setCurrentStep(10);
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  // ============ STEP 10: Publish ============
  const publishTemplate = async () => {
    if (!template) return;
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/templates/${template.id}/publish`,
        { title, description, tags },
        { withCredentials: true }
      );
      toast.success("Template published!");
      navigate("/admin/templates");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally {
      setLoading(false);
    }
  };

  // ============ RENDER STEPS ============
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">Upload Background for Each Scene</Label>
            <div className="space-y-4">
              {Array.from({ length: sceneCount }).map((_, i) => (
                <div key={i} className="border rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Scene {i + 1}</p>
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all hover:border-primary/50 ${
                      bgPreviews[i] ? "border-primary bg-primary/5" : "border-border"
                    }`}
                    onClick={() => document.getElementById(`bg-upload-${i}`).click()}
                    data-testid={`bg-upload-zone-${i}`}
                  >
                    {bgPreviews[i] ? (
                      <img src={bgPreviews[i]} alt={`Scene ${i + 1} bg`} className="max-h-40 mx-auto rounded-lg" />
                    ) : (
                      <div className="space-y-1">
                        <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Click to upload</p>
                      </div>
                    )}
                    <input id={`bg-upload-${i}`} type="file" accept="image/*" className="hidden" onChange={(e) => handleBgChange(i, e)} />
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={uploadAllBackgrounds} disabled={loading} className="w-full" data-testid="upload-bgs-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Upload & Continue
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">Text Overlay for Each Scene</Label>
            <p className="text-sm text-muted-foreground">This text will be visually rendered on the composite image</p>
            {Array.from({ length: sceneCount }).map((_, i) => (
              <div key={i}>
                <Label className="text-sm">Scene {i + 1}</Label>
                <Input
                  value={textOverlays[i] || ""}
                  onChange={(e) => {
                    const arr = [...textOverlays];
                    arr[i] = e.target.value;
                    setTextOverlays(arr);
                  }}
                  placeholder={`Text overlay for scene ${i + 1}...`}
                  data-testid={`text-overlay-${i}`}
                />
              </div>
            ))}
            <Button onClick={saveTextOverlays} disabled={loading} className="w-full" data-testid="save-overlays-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Save & Continue
            </Button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">Product Image</Label>
            <p className="text-sm text-muted-foreground">This product will appear in all scenes</p>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-primary/50 ${
                productPreview ? "border-primary bg-primary/5" : "border-border"
              }`}
              onClick={() => document.getElementById("product-upload-pv").click()}
              data-testid="product-upload-zone-pv"
            >
              {productPreview ? (
                <img src={productPreview} alt="Product" className="max-h-48 mx-auto rounded-lg" />
              ) : (
                <div className="space-y-2">
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">Click to upload product image</p>
                </div>
              )}
              <input id="product-upload-pv" type="file" accept="image/*" className="hidden" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) { setProductFile(file); setProductPreview(URL.createObjectURL(file)); }
              }} />
            </div>
            <Button onClick={uploadProduct} disabled={loading || (!productFile && !template?.sample_product_image)} className="w-full" data-testid="upload-product-pv-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              {template?.sample_product_image && !productFile ? "Continue" : "Upload & Continue"}
            </Button>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">Generate Composite Images</Label>
            <p className="text-sm text-muted-foreground">
              Each scene combines: Background + Product + Text Overlay via Gemini 3 Pro Image
            </p>
            {Array.from({ length: sceneCount }).map((_, i) => (
              <div key={i} className="border rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold">Scene {i + 1}</p>
                {/* Reference images */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Background</p>
                    {template?.scene_backgrounds?.[i] && (
                      <img src={template.scene_backgrounds[i]} alt="" className="h-20 mx-auto rounded border" />
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Product</p>
                    {template?.sample_product_image && (
                      <img src={template.sample_product_image} alt="" className="h-20 mx-auto rounded border" />
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Text</p>
                    <p className="text-xs bg-muted rounded p-2 h-20 flex items-center justify-center">{textOverlays[i] || "—"}</p>
                  </div>
                </div>
                <Textarea
                  value={compositePrompts[i] || ""}
                  onChange={(e) => {
                    const arr = [...compositePrompts];
                    arr[i] = e.target.value;
                    setCompositePrompts(arr);
                  }}
                  placeholder={`Prompt for scene ${i + 1} composite...`}
                  className="min-h-[80px] text-sm"
                  data-testid={`composite-prompt-${i}`}
                />
                <Button onClick={() => generateComposite(i)} disabled={loading} size="sm" className="w-full" data-testid={`gen-composite-${i}`}>
                  {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Generate Scene {i + 1}
                </Button>
                {template?.scene_composite_images?.[i] && (
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <img src={template.scene_composite_images[i]} alt={`Composite ${i + 1}`} className="max-h-60 mx-auto rounded-lg border" />
                  </div>
                )}
              </div>
            ))}
            {template?.scene_composite_images?.filter(Boolean).length === sceneCount && (
              <Button onClick={() => setCurrentStep(5)} className="w-full" data-testid="composites-continue-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">Video Generation Prompt per Scene</Label>
            <p className="text-sm text-muted-foreground">Each scene video will be generated using Kling (5 sec per scene)</p>
            {Array.from({ length: sceneCount }).map((_, i) => (
              <div key={i} className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-semibold">Scene {i + 1}</p>
                  {template?.scene_composite_images?.[i] && (
                    <img src={template.scene_composite_images[i]} alt="" className="h-12 rounded border" />
                  )}
                </div>
                <Textarea
                  value={videoPrompts[i] || ""}
                  onChange={(e) => {
                    const arr = [...videoPrompts];
                    arr[i] = e.target.value;
                    setVideoPrompts(arr);
                  }}
                  placeholder={`Video prompt for scene ${i + 1}...`}
                  className="min-h-[80px] text-sm"
                  data-testid={`video-prompt-${i}`}
                />
              </div>
            ))}
            <Button onClick={saveVideoPrompts} disabled={loading} className="w-full" data-testid="save-video-prompts-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Save Prompts & Continue
            </Button>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">Generate Scene Videos</Label>

            {/* Video Method Selector */}
            {isHeroShot ? (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-medium text-sm">Gemini Veo 3.1</div>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Locked</span>
                </div>
                <div className="text-xs text-muted-foreground">Direct Google API — optimized for hero product shots</div>
              </div>
            ) : (
            <div>
              <Label className="text-sm font-medium">Video Generation Method</Label>
              <p className="text-xs text-muted-foreground mb-2">
                This method will also be used when users recreate from this template.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div
                  onClick={() => setVideoMethod("fal_kling")}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    videoMethod === "fal_kling"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="pv-video-method-fal"
                >
                  <div className="font-medium text-sm">Fal AI (Kling)</div>
                  <div className="text-xs text-muted-foreground mt-1">Current Kling workflow via Fal.ai</div>
                </div>
                <div
                  onClick={() => setVideoMethod("gemini_veo")}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    videoMethod === "gemini_veo"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="pv-video-method-gemini"
                >
                  <div className="font-medium text-sm">Gemini Veo 3.1 Fast</div>
                  <div className="text-xs text-muted-foreground mt-1">Direct Google API, native audio</div>
                </div>
                <div
                  onClick={() => setVideoMethod("fal_sora2")}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    videoMethod === "fal_sora2"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="pv-video-method-sora2"
                >
                  <div className="font-medium text-sm">Sora 2</div>
                  <div className="text-xs text-muted-foreground mt-1">OpenAI Sora 2 via Fal.ai — high quality</div>
                </div>
              </div>
            </div>
            )}

            {Array.from({ length: sceneCount }).map((_, i) => {
              const status = sceneJobStatuses[i];
              const videoUrl = template?.scene_videos?.[i];
              return (
                <div key={i} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Scene {i + 1}</p>
                    {videoUrl && <Badge className="bg-green-500">Ready</Badge>}
                    {status?.status === "processing" && <Badge variant="secondary">Generating... {status.progress}%</Badge>}
                    {status?.status === "failed" && <Badge variant="destructive">Failed</Badge>}
                  </div>
                  {videoUrl ? (
                    <div className="space-y-2">
                      <video src={`${videoUrl}?t=${Date.now()}`} controls className="w-full max-h-48 rounded-lg" data-testid={`scene-video-${i}`} />
                      <Button
                        onClick={() => generateSceneVideo(i)}
                        disabled={loading || status?.status === "processing"}
                        size="sm"
                        variant="outline"
                        className="w-full"
                        data-testid={`regen-video-${i}`}
                      >
                        {status?.status === "processing" ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{status.message}</>
                        ) : (
                          <><RefreshCw className="w-3 h-3 mr-1" />Regenerate Scene {i + 1}</>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => generateSceneVideo(i)} disabled={loading || status?.status === "processing"} size="sm" className="w-full" data-testid={`gen-video-${i}`}>
                      {status?.status === "processing" ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{status.message}</>
                      ) : (
                        <><Video className="w-3 h-3 mr-1" />Generate Scene {i + 1}</>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
            {template?.scene_videos?.filter(Boolean).length === sceneCount && (
              <Button onClick={() => setCurrentStep(7)} className="w-full" data-testid="videos-continue-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">Upload Background Music</Label>
            <p className="text-sm text-muted-foreground">MP3 audio to overlay on the final stitched video</p>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-primary/50 ${
                audioFile || template?.audio_file_url ? "border-primary bg-primary/5" : "border-border"
              }`}
              onClick={() => document.getElementById("audio-upload").click()}
              data-testid="audio-upload-zone"
            >
              {audioFile ? (
                <div className="space-y-2">
                  <Music className="w-10 h-10 mx-auto text-primary" />
                  <p className="text-sm">{audioFile.name}</p>
                </div>
              ) : template?.audio_file_url ? (
                <div className="space-y-2">
                  <Music className="w-10 h-10 mx-auto text-primary" />
                  <p className="text-sm text-muted-foreground">Audio uploaded</p>
                  <audio src={template.audio_file_url} controls className="mx-auto" />
                </div>
              ) : (
                <div className="space-y-2">
                  <Music className="w-10 h-10 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">Click to upload MP3</p>
                </div>
              )}
              <input id="audio-upload" type="file" accept="audio/*" className="hidden" onChange={(e) => setAudioFile(e.target.files?.[0])} />
            </div>
            <Button onClick={audioFile ? uploadAudio : () => setCurrentStep(8)} disabled={loading || (!audioFile && !template?.audio_file_url)} className="w-full" data-testid="upload-audio-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              {template?.audio_file_url && !audioFile ? "Continue" : "Upload & Continue"}
            </Button>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <Label className="text-base font-medium">{isHeroShot ? "Generate Final Preview" : "Stitch & Preview"}</Label>
            <p className="text-sm text-muted-foreground">{isHeroShot ? "Combine scene video with audio overlay" : "Stitch all scene videos together with audio overlay"}</p>
            {template?.sample_video && (
              <div className="p-4 bg-muted/30 rounded-xl border">
                <p className="text-sm font-semibold mb-3 text-center">Preview Video</p>
                <video src={template.sample_video} controls className="w-full max-h-[400px] rounded-lg mx-auto" data-testid="preview-video" />
              </div>
            )}
            {stitchStatus?.status === "processing" && (
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
                <p className="text-sm">{stitchStatus.message} ({stitchStatus.progress}%)</p>
              </div>
            )}
            <Button onClick={stitchPreview} disabled={loading || stitchStatus?.status === "processing"} className="w-full" data-testid="stitch-btn">
              {stitchStatus?.status === "processing" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
              ) : template?.sample_video ? (
                <><Video className="w-4 h-4 mr-2" />{isHeroShot ? "Re-generate Preview" : "Re-stitch Preview"}</>
              ) : (
                <><Video className="w-4 h-4 mr-2" />{isHeroShot ? "Generate Final Preview" : "Stitch Preview Video"}</>
              )}
            </Button>
            {template?.sample_video && (
              <Button onClick={() => setCurrentStep(9)} variant="outline" className="w-full" data-testid="stitch-continue-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Template title" data-testid="pv-title-input" />
            </div>
            <div>
              <Label className="text-base font-medium">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe this template..." className="min-h-[80px]" data-testid="pv-desc-input" />
            </div>
            <div>
              <Label className="text-base font-medium">Product Type</Label>
              <p className="text-sm text-muted-foreground mb-1">Internal label for this product category (not shown to users)</p>
              <Input value={productType} onChange={(e) => setProductType(e.target.value)} placeholder="e.g., Skincare, Electronics, Apparel" data-testid="pv-product-type-input" />
            </div>
            <div>
              <Label className="text-base font-medium">Recreation Credit Cost</Label>
              <p className="text-sm text-muted-foreground mb-1">Credits deducted per recreation. Leave blank for default.</p>
              <Input
                type="number"
                value={creditsCost}
                onChange={(e) => setCreditsCost(e.target.value)}
                placeholder="e.g., 3"
                className="w-40"
                min="0"
                step="0.5"
                data-testid="pv-credits-cost-input"
              />
            </div>
            <div>
              <Label className="text-base font-medium">Tags</Label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => setTags(tags.filter(t => t !== tag))} />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Add tag" onKeyDown={(e) => {
                  if (e.key === "Enter" && newTag.trim()) { setTags([...tags, newTag.trim()]); setNewTag(""); e.preventDefault(); }
                }} />
                <Button size="sm" variant="outline" onClick={() => { if (newTag.trim()) { setTags([...tags, newTag.trim()]); setNewTag(""); } }}>Add</Button>
              </div>
            </div>
            <Button onClick={saveDetails} disabled={loading || !title.trim()} className="w-full" data-testid="pv-save-details-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Save & Continue
            </Button>
          </div>
        );

      case 10:
        return (
          <div className="space-y-6 text-center">
            <h3 className="text-lg font-semibold">Ready to Publish</h3>
            <p className="text-muted-foreground">{title} — {sceneCount} scenes</p>
            {template?.sample_video && (
              <video src={template.sample_video} controls className="max-h-64 mx-auto rounded-lg" />
            )}
            <div className="flex gap-3">
              <Button onClick={() => setCurrentStep(9)} variant="outline" className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" /> Edit Details
              </Button>
              <Button onClick={publishTemplate} disabled={loading} className="flex-1" data-testid="pv-publish-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Publish Template
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* Step indicator */}
      <div className="flex items-center gap-1 overflow-x-auto pb-4 mb-6 border-b border-border">
        {STEPS.map((step) => (
          <button
            key={step.id}
            onClick={() => setCurrentStep(step.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors ${
              currentStep === step.id
                ? "bg-primary text-white font-medium"
                : step.id < currentStep
                ? "bg-green-100 text-green-700"
                : "bg-muted text-muted-foreground"
            }`}
            data-testid={`pv-step-${step.id}`}
          >
            {step.id < currentStep ? <Check className="w-3 h-3" /> : <span>{step.id}</span>}
            <span>{step.title}</span>
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{STEPS.find(s => s.id === currentStep)?.title}</CardTitle>
          <CardDescription>{STEPS.find(s => s.id === currentStep)?.description}</CardDescription>
        </CardHeader>
        <CardContent>{renderStep()}</CardContent>
      </Card>
    </div>
  );
}
